import type {
  IAttendanceDashboardComputedState,
  IAttendanceDashboardConfigStates,
  IAttendanceDashboardParams,
  IAttendanceDashboardRange,
  IAttendanceDashboardResponse,
  IAttendanceDashboardStateDetail,
  IAttendanceDashboardUnavailableUserDetail,
  TAttendanceDashboardState,
} from '@src/models/interfaces/attendanceDashboard.interface';
import type {
  IWorkJourneyManagementAttentionGroup,
  IWorkJourneyManagementAttentionItem,
  IWorkJourneyManagementAttentionListResponse,
  IWorkJourneyManagementCalendarDayConflictItem,
  IWorkJourneyManagementJourneyExtraBadge,
  IWorkJourneyManagementJourneyListItem,
  IWorkJourneyManagementJourneyStatusBadge,
  IWorkJourneyManagementJourneysListParams,
  IWorkJourneyManagementJourneysListResponse,
  IWorkJourneyReplacementCandidate,
  IWorkJourneyReplacementCandidateDto,
  IWorkJourneyReplacementCandidatesListResponse,
  IWorkJourneyReplacementCandidatesListResponseDto,
  TWorkJourneyManagementActionType,
  TWorkJourneyManagementAttentionReason,
  TWorkJourneyManagementJourneyMonthStatusType,
  TWorkJourneyManagementJourneyRowHighlight,
  TWorkJourneyManagementTimeGroup,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { DateUtil } from '@src/packages/utils/dates';
import { ATTENDANCE_DASHBOARD_STATES } from '@src/packages/workJourney/constants';

const TIME_GROUP_ORDER: TWorkJourneyManagementTimeGroup[] = ['this_week', 'next_30_days', 'future'];
const SECONDS_PER_DAY = 86_400;

const STATE_TO_REASON: Record<TAttendanceDashboardState, TWorkJourneyManagementAttentionReason> = {
  understaffed_due_to_absences: 'below_minimum',
  users_unavailable: 'below_minimum',
  rejected_shift_change: 'rejected_change',
  insufficient_assigned_members: 'staffing_shortage',
};

const STATE_TO_ACTION: Record<TAttendanceDashboardState, TWorkJourneyManagementActionType> = {
  understaffed_due_to_absences: 'cover_position',
  users_unavailable: 'cover_position',
  rejected_shift_change: 'resolve',
  insufficient_assigned_members: 'hiring',
};

const EMPTY_DETAILS: IAttendanceDashboardStateDetail = {};

const DASHBOARD_STATE_TO_JOURNEY_STATUS: Record<
  TAttendanceDashboardState,
  TWorkJourneyManagementJourneyMonthStatusType
> = {
  understaffed_due_to_absences: 'below_minimum',
  users_unavailable: 'scheduled_absences',
  insufficient_assigned_members: 'staffing_shortage',
  rejected_shift_change: 'rejections',
};

export class WorkJourneyManagementMapper {
  static resolveTimeGroup(
    scheduledStartAt: Date,
    referenceDate: Date = new Date(),
  ): TWorkJourneyManagementTimeGroup {
    const startOfReferenceDay = DateUtil.getDayBoundsMillis(referenceDate.getTime()).startMillis;
    const scheduledDay = DateUtil.getDayBoundsMillis(scheduledStartAt.getTime()).startMillis;

    const diffDays = Math.floor(
      (scheduledDay - startOfReferenceDay) / (SECONDS_PER_DAY * 1000),
    );

    if (diffDays <= 6) return 'this_week';
    if (diffDays <= 30) return 'next_30_days';

    return 'future';
  }

  private static mapComputedStateToItem(
    config: IAttendanceDashboardConfigStates,
    computed: IAttendanceDashboardComputedState,
    date: string | null,
    referenceDate: Date,
  ): IWorkJourneyManagementAttentionItem {
    const timeGroup = date
      ? this.resolveTimeGroup(this.parseDashboardDate(date), referenceDate)
      : 'this_week';

    return {
      id: `${config.workJourneyConfigId}-${date ?? 'config'}-${computed.state}`,
      date,
      dateFrom: date,
      dateTo: date,
      workJourneyConfigId: config.workJourneyConfigId,
      workJourneyConfigName: config.workJourneyConfigName,
      requiredMembers: config.requiredMembers,
      assignedMembers: config.assignedMembers,
      state: computed.state,
      reason: STATE_TO_REASON[computed.state],
      actionType: STATE_TO_ACTION[computed.state],
      timeGroup,
      details: computed.details ?? EMPTY_DETAILS,
    };
  }

  static fromAttendanceDashboard(
    response: IAttendanceDashboardResponse | null,
    referenceDate: Date = new Date(),
  ): { items: IWorkJourneyManagementAttentionItem[]; total: number } {
    const configs = response?.states?.configs ?? [];
    const items: IWorkJourneyManagementAttentionItem[] = [];

    configs.forEach(config => {
      (config.configStates ?? []).forEach(computed => {
        items.push(this.mapComputedStateToItem(config, computed, null, referenceDate));
      });
      (config.days ?? []).forEach(day => {
        this.mergeDayStatesForAttention(day.states ?? []).forEach(computed => {
          items.push(this.mapComputedStateToItem(config, computed, day.date, referenceDate));
        });
      });
    });

    return { items, total: items.length };
  }

  /** Maps listAttention CF rows, filling `date` from `dateFrom`. */
  static fromAttentionListItem(
    item: IWorkJourneyManagementAttentionItem,
  ): IWorkJourneyManagementAttentionItem {
    const dateFrom = item.dateFrom ?? item.date ?? null;
    const dateTo = item.dateTo ?? dateFrom;

    return {
      ...item,
      date: dateFrom,
      dateFrom,
      dateTo,
      details: item.details ?? EMPTY_DETAILS,
    };
  }

  static fromAttentionListResponse(
    response: IWorkJourneyManagementAttentionListResponse | null | undefined,
  ): { items: IWorkJourneyManagementAttentionItem[]; total: number } {
    const items = (response?.items ?? []).map(item => this.fromAttentionListItem(item));
    return { items, total: response?.total ?? items.length };
  }

  /** Maps listCalendar day.conflicts into attention items (adds from/to). */
  static fromCalendarDayConflicts(
    conflicts: IWorkJourneyManagementCalendarDayConflictItem[] | null | undefined,
  ): IWorkJourneyManagementAttentionItem[] {
    return (conflicts ?? []).map((item: IWorkJourneyManagementCalendarDayConflictItem) => ({
      id: item.id,
      date: item.date,
      dateFrom: item.date,
      dateTo: item.date,
      workJourneyConfigId: item.workJourneyConfigId,
      workJourneyConfigName: item.workJourneyConfigName,
      requiredMembers: item.requiredMembers,
      assignedMembers: item.assignedMembers,
      state: item.state,
      reason: item.reason,
      actionType: item.actionType,
      details: item.details ?? EMPTY_DETAILS,
    }));
  }

  /**
   * When both understaffed and users_unavailable exist on the same day, keep a
   * single attention row (understaffed) and union absentUsers with unavailable
   * users so the cover drawer lists everyone who may need replacement.
   */
  private static mergeUnavailableUsers(
    primary: IAttendanceDashboardUnavailableUserDetail[] | undefined,
    secondary: IAttendanceDashboardUnavailableUserDetail[] | undefined,
  ): IAttendanceDashboardUnavailableUserDetail[] | undefined {
    const merged: IAttendanceDashboardUnavailableUserDetail[] = [];
    const seenEmployeeIds = new Set<string>();

    [...(primary ?? []), ...(secondary ?? [])].forEach(user => {
      if (!user.employeeId || seenEmployeeIds.has(user.employeeId)) {
        return;
      }
      seenEmployeeIds.add(user.employeeId);
      merged.push(user);
    });

    return merged.length > 0 ? merged : undefined;
  }

  private static mergeDayStatesForAttention(
    states: IAttendanceDashboardComputedState[],
  ): IAttendanceDashboardComputedState[] {
    const {
      UNDERSTAFFED_DUE_TO_ABSENCES,
      USERS_UNAVAILABLE,
    } = ATTENDANCE_DASHBOARD_STATES;
    const understaffed = states.find(computed => computed.state === UNDERSTAFFED_DUE_TO_ABSENCES);
    const unavailable = states.find(computed => computed.state === USERS_UNAVAILABLE);

    if (!understaffed || !unavailable) {
      return states;
    }

    return states
      .filter(computed => computed.state !== USERS_UNAVAILABLE)
      .map(computed => {
        if (computed.state !== UNDERSTAFFED_DUE_TO_ABSENCES) {
          return computed;
        }

        return {
          ...computed,
          details: {
            ...computed.details,
            absentUsers: this.mergeUnavailableUsers(
              computed.details?.absentUsers,
              unavailable.details?.unavailableUsers,
            ),
          },
        };
      });
  }

  static groupByTimeGroup(
    AttentionItems: IWorkJourneyManagementAttentionItem[],
  ): IWorkJourneyManagementAttentionGroup[] {
    return TIME_GROUP_ORDER.map(timeGroup => {
      const items = AttentionItems.filter(item => item.timeGroup === timeGroup);
      const pendingCount = items.length;
      return { timeGroup, items, pendingCount };
    }).filter(group => group.items.length > 0);
  }

  /**
   * Range from start of today (app timezone) in Unix milliseconds.
   * Backend `normalizeRangeToEpochSeconds` converts ms → epoch seconds for ES.
   */
  static getAttentionItemsQueryRange(): IAttendanceDashboardRange {
    return { gte: DateUtil.getDayBoundsMillis().startMillis };
  }

  /** Inclusive app-timezone month range in Unix milliseconds for `attendanceDashboard`. */
  static getMonthQueryRange(month: number, year: number): IAttendanceDashboardRange {
    const { startMillis, endMillis } = DateUtil.getMonthBoundsMillis(month, year);

    return {
      gte: startMillis,
      lte: endMillis,
    };
  }

  /** Inclusive app-timezone day range (`YYYY-MM-DD`) in Unix milliseconds for `attendanceDashboard`. */
  static getDayQueryRange(date: string): IAttendanceDashboardRange {
    const bounds = DateUtil.getCalendarDateBoundsMillis(date);
    if (!bounds) {
      return { gte: 0, lte: 0 };
    }

    return {
      gte: bounds.startMillis,
      lte: bounds.endMillis,
    };
  }

  private static countDaysWithState(
    config: IAttendanceDashboardConfigStates,
    state: TAttendanceDashboardState,
  ): number {
    return (config.days ?? []).filter(day =>
      (day.states ?? []).some(computed => computed.state === state),
    ).length;
  }

  private static sumAbsentWorkers(config: IAttendanceDashboardConfigStates): number {
    const { UNDERSTAFFED_DUE_TO_ABSENCES } = ATTENDANCE_DASHBOARD_STATES;
    return (config.days ?? []).reduce((total, day) => {
      const understaffed = (day.states ?? []).find(
        computed => computed.state === UNDERSTAFFED_DUE_TO_ABSENCES,
      );
      return total + (understaffed?.details?.absentWorkers ?? 0);
    }, 0);
  }

  private static buildJourneyMonthStatus(
    config: IAttendanceDashboardConfigStates,
  ): IWorkJourneyManagementJourneyStatusBadge[] {
    const {
      UNDERSTAFFED_DUE_TO_ABSENCES,
      REJECTED_SHIFT_CHANGE,
      INSUFFICIENT_ASSIGNED_MEMBERS,
      USERS_UNAVAILABLE,
    } = ATTENDANCE_DASHBOARD_STATES;
    const monthStatus: IWorkJourneyManagementJourneyStatusBadge[] = [];
    const understaffedDays = this.countDaysWithState(config, UNDERSTAFFED_DUE_TO_ABSENCES);
    const unavailableDays = this.countDaysWithState(config, USERS_UNAVAILABLE);
    const rejectionDays = this.countDaysWithState(config, REJECTED_SHIFT_CHANGE);
    const hasStaffingShortage = (config.configStates ?? []).some(
      computed => computed.state === INSUFFICIENT_ASSIGNED_MEMBERS,
    );

    if (understaffedDays > 0) {
      const type = DASHBOARD_STATE_TO_JOURNEY_STATUS.understaffed_due_to_absences;
      monthStatus.push({ type, dayCount: understaffedDays });
    }

    if (unavailableDays > 0) {
      const type = DASHBOARD_STATE_TO_JOURNEY_STATUS.users_unavailable;
      monthStatus.push({ type, dayCount: unavailableDays });
    }

    if (hasStaffingShortage) {
      const type = DASHBOARD_STATE_TO_JOURNEY_STATUS.insufficient_assigned_members;
      monthStatus.push({ type });
    }

    if (rejectionDays > 0) {
      const type = DASHBOARD_STATE_TO_JOURNEY_STATUS.rejected_shift_change;
      monthStatus.push({ type, dayCount: rejectionDays });
    }

    if (monthStatus.length === 0) {
      monthStatus.push({ type: 'on_track' });
    }

    return monthStatus;
  }

  private static resolveJourneyRowHighlight(
    monthStatus: IWorkJourneyManagementJourneyStatusBadge[],
  ): TWorkJourneyManagementJourneyRowHighlight {
    const types = new Set(monthStatus.map(status => status.type));
    if (types.has('below_minimum') || types.has('staffing_shortage')) return 'critical';
    if (types.has('scheduled_absences') || types.has('rejections')) return 'warning';
    return 'normal';
  }

  private static mapConfigToJourneyItem(
    config: IAttendanceDashboardConfigStates,
    organizationId: string,
  ): IWorkJourneyManagementJourneyListItem {
    const monthStatus = this.buildJourneyMonthStatus(config);
    const hasStaffingShortage = monthStatus.some(status => status.type === 'staffing_shortage');
    const overstaffedBy = config.assignedMembers - config.requiredMembers;
    const extraBadges: IWorkJourneyManagementJourneyExtraBadge[] | undefined =
      overstaffedBy > 0 ? [{ label: `+${overstaffedBy}`, type: 'overstaffed' }] : undefined;

    return {
      workJourneyConfig: {
        id: config.workJourneyConfigId,
        name: config.workJourneyConfigName,
        organizationId,
        requiredMembers: config.requiredMembers,
        state: 'active',
      },
      shiftDescription: '',
      monthStatus,
      coverage: hasStaffingShortage
        ? null
        : {
            current: config.assignedMembers,
            required: config.requiredMembers,
          },
      extraBadges,
      rowHighlight: this.resolveJourneyRowHighlight(monthStatus),
    };
  }

  static fromAttendanceDashboardToJourneys(
    response: IAttendanceDashboardResponse | null,
    params: Pick<IWorkJourneyManagementJourneysListParams, 'organizationId' | 'month' | 'year'>,
  ): IWorkJourneyManagementJourneysListResponse {
    const configs = response?.states?.configs ?? [];
    const items = configs.map(config => this.mapConfigToJourneyItem(config, params.organizationId));
    let belowMinimumCount = 0;
    let scheduledAbsencesCount = 0;
    let rejectionsCount = 0;

    configs.forEach(config => {
      const understaffedDays = this.countDaysWithState(config, 'understaffed_due_to_absences');
      const unavailableDays = this.countDaysWithState(config, 'users_unavailable');
      const rejectionDays = this.countDaysWithState(config, 'rejected_shift_change');
      if (understaffedDays > 0) belowMinimumCount += 1;
      if (rejectionDays > 0) rejectionsCount += 1;
      if (unavailableDays > 0) {
        scheduledAbsencesCount += unavailableDays;
      } else {
        scheduledAbsencesCount += this.sumAbsentWorkers(config);
      }
    });

    return {
      summary: {
        month: params.month,
        year: params.year,
        issues: [
          { type: 'below_minimum', count: belowMinimumCount },
          { type: 'scheduled_absences', count: scheduledAbsencesCount },
          { type: 'rejections', count: rejectionsCount },
        ],
      },
      items,
      total: items.length,
    };
  }

  static toAttendanceDashboardParams(
    organizationId: string,
    range: IAttendanceDashboardRange,
    workJourneyConfigId?: string,
  ): IAttendanceDashboardParams {
    const params: IAttendanceDashboardParams = { organizationId, range };

    if (workJourneyConfigId && workJourneyConfigId !== 'all') {
      params.workJourneyConfigIds = [workJourneyConfigId];
    }

    return params;
  }

  static parseDashboardDate(date: string): Date {
    const bounds = DateUtil.getCalendarDateBoundsMillis(date);
    return bounds ? new Date(bounds.startMillis) : new Date(0);
  }

  static toUnixMilliseconds(date: Date): number {
    return Math.floor(date.getTime());
  }

  static fromReplacementCandidateDto(
    dto: IWorkJourneyReplacementCandidateDto,
  ): IWorkJourneyReplacementCandidate {
    const firstName = dto.firstName || '';
    const lastName = dto.lastName || '';
    const displayName =
      dto.displayName || `${firstName} ${lastName}`.trim() || dto.email || dto.id || '';

    return {
      id: dto.id,
      displayName,
      email: dto.email || '',
      firstName,
      lastName,
      idCard: dto.idCard || '',
      jurisdictionId: dto.jurisdictionId,
      photoURL: dto.photoURL || null,
      job: dto.job
        ? {
            id: dto.job.id,
            name: dto.job.name || '',
          }
        : null,
      currentWorkJourney: dto.currentWorkJourney
        ? {
            workJourneyConfigId: dto.currentWorkJourney.workJourneyConfigId,
            workJourneyConfigName: dto.currentWorkJourney.workJourneyConfigName ?? null,
          }
        : null,
    };
  }

  static fromReplacementCandidatesResponse(
    response: IWorkJourneyReplacementCandidatesListResponseDto | null | undefined,
  ): IWorkJourneyReplacementCandidatesListResponse {
    const users = (response?.users ?? []).map(user =>
      WorkJourneyManagementMapper.fromReplacementCandidateDto(user),
    );

    return {
      users,
      total: response?.total ?? users.length,
    };
  }
}
